import React, { useState, useCallback } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { URLS } from '@wix/da-url';
import {
  BiData,
  BuyCoreButtonBiEvent,
  CustomBoxPreviewBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import CoreIcon from '@wix/da-ds/pkg/Icons/24x24/Core';
import CoreBadge from '@wix/da-ds/pkg/Badges/CoreBadge';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import InfoBlock, { Props as InfoBlockProps } from '@wix/da-ds/pkg/InfoBlock';
import PreviewModeBar from '@wix/da-ds/pkg/PreviewModeBar';

import s from './WidgetPaywall.scss';

export interface Props extends InfoBlockProps {
  className?: string;
  children: React.ReactNode;
}

enum WidgetPaywallView {
  LOCKED = 'locked',
  PREVIEW = 'preview',
}

export const WidgetPaywall: React.FC<Props> = ({
  className,
  children,
  ...emptyStateProps
}) => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState(WidgetPaywallView.LOCKED);
  const showPreview = useCallback(
    () => setActiveView(WidgetPaywallView.PREVIEW),
    []
  );
  const showLocked = useCallback(
    () => setActiveView(WidgetPaywallView.LOCKED),
    []
  );
  return (
    <ErrorBoundary debugComponent="WidgetPaywall">
      <div className={c(s['root'], className)}>
        {activeView === WidgetPaywallView.LOCKED && (
          <div className={s['overlay']}>
            <InfoBlock
              className={s['locked']}
              backgroundType="none"
              title={t('paywall.widgetPreview.title')}
              subtitle={t('paywall.widgetPreview.subtitle')}
              illustration={
                <>
                  <CoreBadge size="large" className={s['core-badge']} />{' '}
                  {t('paywall.widgetPreview.icon.suffix')}
                </>
              }
              customButton={
                <BrandedButton
                  variant="core-orange"
                  size="large"
                  href={URLS.CORE_MEMBERSHIP}
                  biData={BiData<BuyCoreButtonBiEvent>({
                    evid: 375,
                    referral_info: ReferralType.WIDGET_PAYWALL_OVERLAY,
                  })}
                >
                  {t('paywall.widgetPreview.button.upgrade')}
                </BrandedButton>
              }
              customSecondaryButton={
                <GhostButton
                  size="medium"
                  variant="main"
                  onClick={showPreview}
                  biData={BiData<CustomBoxPreviewBiEvent>({
                    evid: 268,
                  })}
                >
                  {t('paywall.widgetPreview.button.preview')}
                </GhostButton>
              }
              {...emptyStateProps}
            />
          </div>
        )}
        {activeView === WidgetPaywallView.PREVIEW && (
          <div className={c(s['overlay'], s['transparent-overlay'])}>
            <div className={s['preview']}>
              <PreviewModeBar
                onClose={showLocked}
                messageBeforeIcon={t(
                  'paywall.widgetPreview.snackbar.pushThisLive'
                )}
                icon={<CoreIcon size={IconSize.SMALL} disableDefaultColors />}
                ctaHref={URLS.CORE_MEMBERSHIP}
                ctaBiData={BiData<BuyCoreButtonBiEvent>({
                  evid: 375,
                  referral_info: ReferralType.WIDGET_PAYWALL_PREVIEW,
                })}
                ctaLabel={t('paywall.widgetPreview.snackbar.upgradeToPublish')}
              />
            </div>
          </div>
        )}
        {children}
      </div>
    </ErrorBoundary>
  );
};
WidgetPaywall.displayName = 'WidgetPaywall';

export default WidgetPaywall;
